/* eslint-disable @next/next/no-img-element */
"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { lazy, Suspense, useEffect, useRef, useState, type MouseEvent } from "react";
import type { AuthDialogMode, RegistrationRole } from "@/modules/auth/components/AuthDialog";
import { coreServices } from "@/shared/config/coreServices";
import { isSiteActive } from "@/shared/config/customerSupport";
import { useAuthStore } from "@/shared/store/authStore";

const AuthenticatedLandingHeader = lazy(() =>
  import("./AuthenticatedLandingHeader").then((module) => ({ default: module.AuthenticatedLandingHeader })),
);

type LandingHeaderProps = {
  authDialogOpen?: boolean;
  onAuthRequest: (mode: AuthDialogMode, role?: RegistrationRole) => void;
};

const FIGMA_ASSETS = {
  logoMark: "/assets/brand/logo-mark-7cc959d9-5283-46c3-92ec-5f8be3d1ffea.svg",
  logoType: "/assets/brand/logo-type-493879ef-c62f-4114-b3fe-6882487aadbc.svg",
  portalIcon: "/assets/landingHeader/portal-icon-e066ad3f-9b90-4373-be93-cecb5fa71eec.svg",
};
const PROVIDER_PAGE_HREF = "/for-providers";

type LockedDropdown = "services" | null;

function ProviderNavIcon({ className }: { className?: string }) {
  return (
    <svg aria-hidden="true" className={className} fill="none" viewBox="0 0 24 24">
      <path d="M9 5.75h6.25a2 2 0 0 1 2 2v8.5a2 2 0 0 1-2 2H8.75a2 2 0 0 1-2-2V8.5" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" />
      <path d="M9.5 4.75a2.25 2.25 0 0 1 4.5 0v1H9.5v-1Z" stroke="currentColor" strokeLinejoin="round" strokeWidth="1.8" />
      <path d="M12 9.5v5" stroke="currentColor" strokeLinecap="round" strokeWidth="1.8" />
      <path d="M9.5 12h5" stroke="currentColor" strokeLinecap="round" strokeWidth="1.8" />
    </svg>
  );
}

function scrollToTarget(hash: string) {
  const targetId = hash.replace("#", "");
  const target = document.getElementById(targetId);

  if (!target) {
    return;
  }

  const header = document.querySelector(".cv-wireframe .site-header") as HTMLElement | null;
  const headerOffset = header?.getBoundingClientRect().height ?? 0;
  const targetTop = window.scrollY + target.getBoundingClientRect().top - headerOffset - 10;

  window.history.pushState(null, "", hash);
  window.scrollTo({ top: Math.max(targetTop, 0), behavior: "smooth" });
}

function scrollToHash(event: MouseEvent<HTMLAnchorElement>, href: string) {
  const hashIndex = href.indexOf("#");

  if (hashIndex < 0 || typeof window === "undefined") {
    return;
  }

  const hash = href.slice(hashIndex);
  const target = document.getElementById(hash.replace("#", ""));
  const canScrollOnCurrentPage = href.startsWith("#") || window.location.pathname === "/";

  if (target && canScrollOnCurrentPage) {
    event.preventDefault();
    scrollToTarget(hash);
  }
}

export function LandingHeader({ authDialogOpen = false, onAuthRequest }: LandingHeaderProps) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const pathname = usePathname();
  const activeSection = pathname?.startsWith("/services")
    ? "services"
    : pathname?.startsWith("/terms-and-conditions")
      ? "terms"
      : pathname?.startsWith("/privacy-policy")
        ? "privacy"
        : "";
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const [lockedDropdown, setLockedDropdown] = useState<LockedDropdown>(null);
  const headerInnerRef = useRef<HTMLDivElement | null>(null);
  const isProviderPage = pathname?.startsWith(PROVIDER_PAGE_HREF) ?? false;
  const printTitle = activeSection === "terms"
    ? "Terms & Conditions | Carevinco"
    : activeSection === "privacy"
      ? "Privacy Policy | Carevinco"
      : "";

  useEffect(() => {
    if (pathname !== "/" || typeof window === "undefined" || !window.location.hash) {
      return undefined;
    }

    const hash = window.location.hash;
    const targetId = hash.replace("#", "");
    let frame = 0;
    let observer: MutationObserver | null = null;

    function scrollWhenReady() {
      if (!document.getElementById(targetId)) {
        return false;
      }

      frame = window.requestAnimationFrame(() => scrollToTarget(hash));
      return true;
    }

    if (!scrollWhenReady()) {
      observer = new MutationObserver(() => {
        if (scrollWhenReady()) {
          observer?.disconnect();
        }
      });
      observer.observe(document.body, { childList: true, subtree: true });
    }

    return () => {
      observer?.disconnect();
      window.cancelAnimationFrame(frame);
    };
  }, [pathname]);

  useEffect(() => {
    function handleDocumentClick(event: globalThis.MouseEvent) {
      if (!lockedDropdown) {
        return;
      }

      const target = event.target as Node | null;

      if (target && headerInnerRef.current?.contains(target)) {
        return;
      }

      setLockedDropdown(null);
    }

    function handleEscape(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setLockedDropdown(null);
        setIsMobileNavOpen(false);
      }
    }

    document.addEventListener("click", handleDocumentClick);
    window.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("click", handleDocumentClick);
      window.removeEventListener("keydown", handleEscape);
    };
  }, [lockedDropdown]);

  if (isAuthenticated) {
    return (
      <Suspense fallback={null}>
        <AuthenticatedLandingHeader onAuthRequest={onAuthRequest} />
      </Suspense>
    );
  }

  function closeAllNavigation() {
    setIsMobileNavOpen(false);
    setLockedDropdown(null);
  }

  function closeMobileNav() {
    setIsMobileNavOpen(false);
  }

  function handleSectionClick(event: MouseEvent<HTMLAnchorElement>, href: string) {
    scrollToHash(event, href);
    closeAllNavigation();
  }

  function handleAuthClick() {
    closeAllNavigation();
    onAuthRequest("login");
  }

  function toggleDropdown(name: Exclude<LockedDropdown, null>) {
    setLockedDropdown((current) => (current === name ? null : name));
  }

  function navClass(sectionId: string, extraClass = "") {
    const isActive = pathname === "/" ? sectionId === "home" : activeSection === sectionId;
    const classes = [extraClass, isActive ? "is-active" : ""].filter(Boolean);
    return classes.join(" ") || undefined;
  }

  const serviceLinks = coreServices.map((service) => (
    <Link href={`/services/${service.slug}`} key={service.slug} role="menuitem" onClick={closeAllNavigation}>
      <strong>{service.title}</strong>
      {/* <span>{service.summary}</span> */}
    </Link>
  ));

  return (
    <header className="site-header">
      <div className="site-header__inner" ref={headerInnerRef}>
        <Link className="brand" href="/#home" onClick={(event) => handleSectionClick(event, "/#home")}>
          <img className="brand__mark" src={FIGMA_ASSETS.logoMark} alt="" height={46} width={51} />
          <span className="brand__typewrap">
            <img className="brand__type" src={FIGMA_ASSETS.logoType} alt="Carevinco" fetchPriority="high" height={24} width={148} />
            <small>Empowering Lives with Compassionate Support</small>
          </span>
        </Link>
        {printTitle ? <span className="site-header__print-title">{printTitle}</span> : null}
        <button
          aria-controls="carevinco-mobile-nav"
          aria-expanded={isMobileNavOpen}
          aria-label={isMobileNavOpen ? "Close navigation menu" : "Open navigation menu"}
          className="wireframe-nav-mobile-trigger"
          type="button"
          onClick={() => setIsMobileNavOpen((open) => !open)}
        >
          <span />
          <span />
          <span />
        </button>
        <nav className="main-nav" aria-label="Primary navigation">
          <Link className={navClass("home")} href="/#home" onClick={(event) => handleSectionClick(event, "/#home")}>
            Home
          </Link>

          <div className={`main-nav__dropdown${lockedDropdown === "services" ? " is-menu-locked" : ""}`}>
            <button
              aria-expanded={lockedDropdown === "services"}
              aria-haspopup="menu"
              className={navClass("services", "main-nav__dropdown-trigger")}
              type="button"
              onClick={() => toggleDropdown("services")}
            >
              Services <span className="main-nav__dropdown-chevron" aria-hidden="true" />
            </button>
            <div className="main-nav__dropdown-menu" role="menu" aria-label="Carevinco services">
              {serviceLinks}
            </div>
          </div>

          <Link className={navClass("terms")} href="/terms-and-conditions" onClick={closeAllNavigation}>
            Terms
          </Link>
          <Link className={navClass("privacy")} href="/privacy-policy" onClick={closeAllNavigation}>
            Privacy
          </Link>
          <Link className={navClass("contact")} href="#contact" onClick={(event) => handleSectionClick(event, "#contact")}>
            Contact Us
          </Link>
          <Link aria-current={isProviderPage ? "page" : undefined} className="portal-btn portal-btn--provider" href={PROVIDER_PAGE_HREF} onClick={closeAllNavigation}>
            <ProviderNavIcon className="portal-btn__svg" />
            For Providers
          </Link>
          {isSiteActive ? (
            <button aria-expanded={authDialogOpen} aria-haspopup="dialog" className="portal-btn portal-btn--auth" type="button" onClick={handleAuthClick}>
              <img src={FIGMA_ASSETS.portalIcon} alt="" height={24} width={24} />
              Get Started
            </button>
          ) : null}
        </nav>
      </div>
      <div aria-hidden={!isMobileNavOpen} className={`wireframe-nav-mobile-backdrop${isMobileNavOpen ? " is-open" : ""}`} onClick={closeMobileNav} />
      <nav
        aria-label="Mobile primary navigation"
        className={`wireframe-mobile-nav${isMobileNavOpen ? " is-open" : ""}`}
        id="carevinco-mobile-nav"
      >
        <div className="wireframe-mobile-nav__head">
          <span>Menu</span>
          <button aria-label="Close navigation menu" type="button" onClick={closeMobileNav}>×</button>
        </div>
        <Link className={navClass("home")} href="/#home" onClick={(event) => handleSectionClick(event, "/#home")}>
          Home
        </Link>
        <details className={`wireframe-mobile-nav__services${activeSection === "services" ? " is-active" : ""}`}>
          <summary>Services <span aria-hidden="true" /></summary>
          <div>{serviceLinks}</div>
        </details>
        <Link className={navClass("terms")} href="/terms-and-conditions" onClick={closeAllNavigation}>
          Terms &amp; Conditions
        </Link>
        <Link className={navClass("privacy")} href="/privacy-policy" onClick={closeAllNavigation}>
          Privacy Policy
        </Link>
        <Link className={navClass("contact")} href="#contact" onClick={(event) => handleSectionClick(event, "#contact")}>
          Contact Us
        </Link>
        <Link aria-current={isProviderPage ? "page" : undefined} className="portal-btn portal-btn--provider" href={PROVIDER_PAGE_HREF} onClick={closeAllNavigation}>
          <ProviderNavIcon className="portal-btn__svg" />
          For Providers
        </Link>
        {isSiteActive ? (
          <button aria-expanded={authDialogOpen} aria-haspopup="dialog" className="portal-btn portal-btn--auth" type="button" onClick={handleAuthClick}>
            <img src={FIGMA_ASSETS.portalIcon} alt="" height={24} width={24} />
            Get Started
          </button>
        ) : null}
      </nav>
    </header>
  );
}
